import HBButton from "@/components/HBButton";
import HBIcon from "@/components/HBIcon";
import { useLogin } from "@/components/LoginModal";
import ShareDropdown from "@/components/ShareDropdown";
import useAppStore from "@/context/store";
import { type IFilterTopicData } from "@/pages/topics/[urlname]";
import { followTopic, unfollowTopic } from "@/services";
import imageUrl from "@/utils/imageUrl";
import strEllipsis from "@/utils/strEllipsis";
import { Button } from "antd";
import classNames from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Else, If, Then } from "react-if";
import styles from "./index.module.less";

function TopicBannerShareButton({ topic, children, placement }) {
  /**分享到其他平台携带的图片 */
  const imgs = useMemo(() => {
    return (
      topic?.contents
        ?.slice(0, 3) //分享微博第三方平台，可传三张图片
        ?.map((content) => imageUrl(content.img, { width: 240 })) || []
    );
  }, [topic]);

  return (
    <ShareDropdown
      placement={placement}
      pics={imgs}
      title={`这个主题真赞！${strEllipsis(topic?.title, 45)}`}
    >
      {children}
    </ShareDropdown>
  );
}

interface ITopicActionsProps {
  topic: IFilterTopicData;
  reverse?: boolean;
  urlname: string;
  showFollow?: boolean;
  isRemix?: boolean;
}

export default function TopicActions({
  topic,
  reverse,
  urlname,
  showFollow = true,
  isRemix = false,
}: ITopicActionsProps) {
  const [following, setFollowing] = useState(false);
  const user = useAppStore((s) => s.user);
  const { login } = useLogin();

  useEffect(() => {
    setFollowing(topic?.following || false);
  }, [topic?.following]);

  const onToggleFollow = useCallback(async () => {
    if (!user) {
      const result = await login({
        source_location: "主题关注",
      });
      if (!result) {
        return;
      }
    }

    if (following) {
      const result = await unfollowTopic({ urlname: urlname as string });
      if (!result.err) {
        setFollowing(false);
      }
    } else {
      const result = await followTopic({ urlname: urlname as string });
      if (!result.err) {
        setFollowing(true);
      }
    }
  }, [user, following, login, urlname]);

  return (
    <div
      className={classNames(styles.operation, {
        [styles.reverse]: reverse,
        [styles.remix]: isRemix,
      })}
    >
      <TopicBannerShareButton
        topic={topic}
        placement={reverse ? "bottomLeft" : "bottomRight"}
      >
        <Button
          className={classNames([styles.button, styles.share])}
          type="text"
          title="分享"
          shape="circle"
        >
          <HBIcon className={styles.icon} type="hb_share_new" />
        </Button>
      </TopicBannerShareButton>

      {showFollow && (
        <If condition={following}>
          <Then>
            <Button
              className={styles.button}
              type="primary"
              title="已关注"
              shape="round"
              onClick={onToggleFollow}
              data-gd-click="button_click"
              data-button-name="取消关注"
            >
              已关注
            </Button>
          </Then>
          <Else>
            <HBButton
              className={styles.button}
              type="primary"
              title="关注主题"
              onClick={onToggleFollow}
              data-gd-click="button_click"
              data-button-name="关注主题"
            >
              关注
            </HBButton>
          </Else>
        </If>
      )}
    </div>
  );
}
